/**
 * Where a floating panel goes, given the passage it belongs to.
 *
 * Pulled out of the component because this is the part that can be wrong, and
 * because a `getBoundingClientRect` inside an effect cannot be tested — jsdom
 * measures everything as zero. The rules, each of which is a way the panel
 * ended up somewhere useless:
 *
 * - **Below the passage** when the viewport has room under it, so the officer's
 *   eye travels down from the words to the controls and no further.
 * - **Above it** when it does not, rather than off the bottom of the screen.
 * - **Clamped both ways**, because the passage can be scrolled off the screen
 *   entirely while the panel is open, and a panel that leaves with it takes the
 *   Reject button with it.
 * - **Never off the right edge** — the panel is wider than a paragraph is, and
 *   a passage that starts near the right margin would push it out of view.
 * - **Never under the sticky document bar.** The viewport's usable top is not
 *   zero once something is pinned to it: a passage selected near the top of the
 *   window put the panel behind the bar, which is a panel that is on screen and
 *   unreadable — the worse of the two failures, because nothing about it looks
 *   wrong. `topInset` is that bar's height, measured, never assumed.
 */
export const BESIDE_GUTTER = 12

export interface Box {
  top: number
  bottom: number
  left: number
}

export interface Viewport {
  width: number
  height: number
}

export function placeBeside(
  at: Box,
  panel: { width: number; height: number },
  view: Viewport,
  gutter: number = BESIDE_GUTTER,
  topInset: number = 0,
): { top: number; left: number } {
  const ceiling = topInset + gutter
  const roomBelow = view.height - at.bottom > panel.height + 2 * gutter
  const wanted = roomBelow ? at.bottom + 10 : at.top - panel.height - 10
  // `Math.max(ceiling, …)` on the floor as well as the ceiling: when the panel
  // is taller than the space beneath the bar the two bounds cross, and without
  // it the clamp would pin the panel above the top of the screen.
  return {
    top: Math.min(Math.max(ceiling, wanted), Math.max(ceiling, view.height - panel.height - gutter)),
    left: Math.min(Math.max(gutter, at.left), Math.max(gutter, view.width - panel.width - gutter)),
  }
}
